import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import httpClient from '../lib/httpClient';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LEGACY_TOKEN_KEY = 'token';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshSession = useCallback(async () => {
    try {
      const response = await httpClient.get(`${API}/auth/me`);
      localStorage.removeItem(LEGACY_TOKEN_KEY);
      setUser(response.data);
      return response.data;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const initializeSession = async () => {
      localStorage.removeItem(LEGACY_TOKEN_KEY);
      await refreshSession();
      if (!cancelled) {
        setLoading(false);
      }
    };

    initializeSession();
    return () => {
      cancelled = true;
    };
  }, [refreshSession]);

  const login = useCallback(async () => {
    try {
      const response = await httpClient.get(`${API}/auth/login`, { timeout: 15000 });
      window.location.href = response.data.url;
      return true;
    } catch {
      console.error('Login request failed');
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await httpClient.post(`${API}/auth/logout`);
    } catch {
      // Local state still needs to be cleared if the backend is unavailable.
    } finally {
      localStorage.removeItem(LEGACY_TOKEN_KEY);
      setUser(null);
      setLoading(false);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshSession }}>
      {children}
    </AuthContext.Provider>
  );
};
